import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, MapPin, Sparkles, Pencil, Check, Star, EyeOff, Users, Lock, ShieldCheck, Bell, Shield, AlertTriangle, Settings, ChevronRight, PauseCircle, PlayCircle, FileText } from "lucide-react";
import api, { resolveImg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { pushSupported, isPushEnabled, enablePush, disablePush } from "../lib/push";
import BottomNav from "../components/BottomNav";
import PhotoUploader from "../components/PhotoUploader";
import SelfieVerify from "../components/SelfieVerify";
import ProfileDetailModal from "../components/ProfileDetailModal";
import { Switch } from "../components/ui/switch";
import { useNotifications } from "../context/NotificationContext";
import { COUNTRIES, RESIDENCE_COUNTRIES, INTERESTS, PARTNER_VALUES, MAX_INTERESTS, MIN_INTERESTS, MAX_VALUES, HAS_CHILDREN, MARITAL_STATUS, SMOKING, ALCOHOL, personalLabel } from "../lib/profileOptions";

const CardRadio = ({ options, value, onSelect, group, tone = "terracotta" }) => (
  <div className="grid grid-cols-2 gap-2.5" role="radiogroup" data-testid={group}>
    {options.map((o) => {
      const active = value === o.value;
      return (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={active}
          data-testid={`${group}-${o.value}`}
          onClick={() => onSelect(o.value)}
          className={`flex items-center gap-2.5 px-3 py-3 rounded-2xl border text-left transition-all active:scale-[0.98] ${
            active
              ? tone === "sage"
                ? "bg-kora-sage/10 border-kora-sage"
                : "bg-kora-terracotta/10 border-kora-terracotta"
              : "bg-white border-kora-line hover:border-kora-terracotta/40"
          }`}
        >
          <span className="text-lg leading-none" aria-hidden="true">{o.emoji}</span>
          <span className={`text-sm font-semibold ${active ? "text-kora-ink" : "text-kora-ink/80"}`}>{o.label}</span>
        </button>
      );
    })}
  </div>
);

const EditChip = ({ active, onClick, children, testid, tone = "terracotta" }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
      active
        ? tone === "sage"
          ? "bg-kora-sage text-white border-kora-sage"
          : "bg-kora-terracotta text-white border-kora-terracotta"
        : "bg-white text-kora-ink border-kora-line hover:border-kora-terracotta/40"
    }`}
  >
    {children}
  </button>
);

const Profile = () => {
  const navigate = useNavigate();
  const { user, setUser, logout } = useAuth();
  const [accountOpen, setAccountOpen] = useState(false);
  const [accountStep, setAccountStep] = useState("menu");
  const [stats, setStats] = useState(null);
  const [pauseBusy, setPauseBusy] = useState(false);
  const [delReasons, setDelReasons] = useState({ found: false, other: false });
  const [delMessage, setDelMessage] = useState("");
  const [delBusy, setDelBusy] = useState(false);

  const openAccountMenu = () => { setAccountStep("menu"); setAccountOpen(true); };

  const startDeleteFlow = async () => {
    setAccountStep("retention");
    try {
      const { data } = await api.get("/account/stats");
      setStats(data);
    } catch (e) { setStats(null); }
  };

  const pauseAccount = async () => {
    setPauseBusy(true);
    try {
      await api.post("/account/pause");
      setUser((u) => ({ ...u, paused: true }));
      setAccountOpen(false);
      toast.success("Votre compte est en pause ⏸ Vous êtes invisible pour les autres membres.");
    } catch (e) {
      toast.error("Impossible de mettre le compte en pause.");
    }
    setPauseBusy(false);
  };

  const unpauseAccount = async () => {
    setPauseBusy(true);
    try {
      await api.post("/account/unpause");
      setUser((u) => ({ ...u, paused: false }));
      toast.success("Bon retour ! Votre profil est de nouveau visible 🎉");
    } catch (e) {
      toast.error("Erreur.");
    }
    setPauseBusy(false);
  };

  const confirmDelete = async () => {
    const reasons = [];
    if (delReasons.found) reasons.push("found");
    if (delReasons.other) reasons.push("other");
    if (!reasons.length) { toast.error("Veuillez cocher au moins une raison."); return; }
    if (delReasons.other && !delMessage.trim()) { toast.error("Merci de préciser votre raison."); return; }
    setDelBusy(true);
    try {
      await api.post("/account/delete", { reasons, message: delMessage.trim() });
      toast.success("Votre compte a été supprimé. À bientôt sur KoraLove 💛");
      setTimeout(() => logout(), 1200);
    } catch (e) {
      toast.error(e.response?.data?.detail || "La suppression a échoué.");
      setDelBusy(false);
    }
  };

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [residenceCountry, setResidenceCountry] = useState(user?.residence_country || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [city, setCity] = useState(user?.city || "");
  const [goal, setGoal] = useState(user?.goal || "amicale");
  const [bodyType, setBodyType] = useState(user?.body_type || "");
  const [height, setHeight] = useState(user?.height || "");
  const [education, setEducation] = useState(user?.education || "");
  const [interests, setInterests] = useState(user?.interests || []);
  const [partnerValues, setPartnerValues] = useState(user?.partner_values || []);
  const [hasChildren, setHasChildren] = useState(user?.has_children || "");
  const [maritalStatus, setMaritalStatus] = useState(user?.marital_status || "");
  const [smoking, setSmoking] = useState(user?.smoking || "");
  const [alcohol, setAlcohol] = useState(user?.alcohol || "");
  const [visits, setVisits] = useState({ count: 0, profiles: [] });
  const [showVerify, setShowVerify] = useState(false);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const { markSeen, refresh } = useNotifications() || {};
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    if (pushSupported()) isPushEnabled().then(setPushOn);
  }, []);

  const togglePush = async (val) => {
    if (pushBusy) return;
    if (!pushSupported()) {
      toast.error("Votre navigateur ne supporte pas les notifications.");
      return;
    }
    setPushBusy(true);
    try {
      if (val) {
        await enablePush();
        setPushOn(true);
        toast.success("Notifications activées 🔔");
      } else {
        await disablePush();
        setPushOn(false);
        toast.success("Notifications désactivées");
      }
    } catch (e) {
      if (e.message === "denied") toast.error("Autorisez les notifications dans votre navigateur.");
      else toast.error("Impossible de modifier les notifications.");
    }
    setPushBusy(false);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/visits");
        setVisits(data);
      } catch (e) {}
    })();
    markSeen?.("visits");
  }, []);

  const onPhotos = (next) => {
    setUser((u) => ({ ...u, photos: next, photo_url: next[0] || u?.photo_url }));
  };

  const toggleInvisible = async (val) => {
    setUser((u) => ({ ...u, invisible_mode: val }));
    try {
      await api.put("/profile", { invisible_mode: val });
      toast.success(val ? "Mode invisible activé 🙈" : "Mode invisible désactivé");
    } catch (e) {
      toast.error("Erreur.");
      setUser((u) => ({ ...u, invisible_mode: !val }));
    }
  };

  const toggleList = (value, list, setList, max) => {
    if (list.includes(value)) {
      setList(list.filter((v) => v !== value));
    } else {
      if (list.length >= max) {
        toast.error(`Maximum ${max} sélections.`);
        return;
      }
      setList([...list, value]);
    }
  };

  const save = async () => {
    if (!name.trim()) {
      toast.error("Le prénom ne peut pas être vide.");
      return;
    }
    try {
      const { data } = await api.put("/profile", {
        name: name.trim(), bio, marital_status: maritalStatus, has_children: hasChildren,
        ...(city ? { city } : {}), ...(residenceCountry ? { residence_country: residenceCountry } : {}),
      });
      setUser(data);
      setEditing(false);
      toast.success("Profil mis à jour.");
    } catch (e) {
      toast.error("Erreur de mise à jour.");
    }
  };

  const handleVisitorAction = async (action) => {
    const p = selectedVisitor;
    if (!p) return;
    setSelectedVisitor(null);
    try {
      const { data } = await api.post("/swipe", { target_id: p.user_id, action });
      if (data.premium_required) {
        toast("✨ Cette personne vous a déjà liké(e) ! Passez Premium pour matcher.", {
          action: { label: "Premium", onClick: () => navigate("/premium") },
        });
      } else if (data.matched) {
        toast.success(`C'est un match avec ${p.name} 💕`, {
          action: { label: "Discuter", onClick: () => navigate("/matchs") },
        });
      } else if (action === "like") {
        toast.success(`J'aime envoyé à ${p.name} ❤️`);
      } else {
        toast(`Profil passé.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur.");
    }
    refresh?.();
  };

  const handleVisitorMessage = async (p) => {
    if (!p) return;
    setSelectedVisitor(null);
    try {
      const { data } = await api.post("/conversations/start", { target_id: p.user_id });
      navigate(`/chat/${data.match_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible d'ouvrir la conversation.");
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-kora-sand pb-28">
      <div className="relative h-44 bg-gradient-to-br from-kora-terracotta to-kora-ochre">
        <div className="absolute -bottom-12 left-1/2 -translate-x-1/2">
          <img src={resolveImg(user.photo_url || user.picture)} alt={user.name} className="w-28 h-28 rounded-full object-cover border-4 border-kora-sand shadow-lg" />
        </div>
      </div>

      <div className="max-w-md mx-auto px-5 pt-16 text-center">
        {user.paused && (
          <div data-testid="paused-banner" className="mb-4 bg-amber-50 border border-amber-300 rounded-2xl px-4 py-3 flex items-center gap-3 text-left">
            <PauseCircle size={22} className="text-amber-600 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-bold text-amber-800">Votre compte est en pause</p>
              <p className="text-xs text-amber-700">Vous êtes invisible pour les autres membres.</p>
            </div>
            <button data-testid="unpause-btn" onClick={unpauseAccount} disabled={pauseBusy} className="bg-amber-500 text-white rounded-full px-3.5 py-2 text-xs font-bold hover:bg-amber-600 transition-colors disabled:opacity-60 inline-flex items-center gap-1">
              <PlayCircle size={14} /> Réactiver
            </button>
          </div>
        )}
        <div className="flex items-center justify-center gap-2">
          <h1 className="font-display font-black text-2xl text-kora-ink">{user.name}{user.age ? `, ${user.age}` : ""}</h1>
          {user.premium && (
            <span data-testid="premium-badge" className="inline-flex items-center gap-1 bg-gradient-to-r from-kora-terracotta to-kora-ochre text-white px-2.5 py-0.5 rounded-full text-xs font-bold">
              <Sparkles size={12} /> Premium
            </span>
          )}
          {user.verified && (
            <span data-testid="verified-badge" className="inline-flex items-center gap-1 bg-kora-sage text-white px-2.5 py-0.5 rounded-full text-xs font-bold">
              <ShieldCheck size={12} /> Vérifié
            </span>
          )}
        </div>
        <div className="flex items-center justify-center gap-1 text-kora-muted mt-1">
          <MapPin size={15} /> <span className="text-sm">{user.city || "—"}</span>
        </div>

        <div className="mt-3">
          <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${user.goal === "mariage" ? "bg-kora-sage/15 text-kora-sage" : "bg-kora-ochre/15 text-[#C77826]"}`}>
            {user.goal === "mariage" ? "Recherche mariage" : "Recherche amicale"}
          </span>
        </div>

        {!editing ? (
          <>
            <div className="bg-white rounded-2xl p-5 border border-kora-line mt-6 text-left">
              <p className="text-xs font-bold uppercase tracking-wider text-kora-muted mb-1">À propos</p>
              <p className="text-sm text-kora-ink/80">{user.bio || "Ajoutez une bio pour vous présenter."}</p>
              {(user.body_type || user.height || user.weight) && (
                <div className="flex flex-wrap gap-4 mt-3 text-sm text-kora-ink/70">
                  {user.body_type && <span>🧍 {user.body_type}</span>}
                  {user.height && <span>📏 {user.height} cm</span>}
                  {user.weight && <span>⚖️ {user.weight} kg</span>}
                </div>
              )}
            </div>

            {user.interests?.length > 0 && (
              <div className="bg-white rounded-2xl p-5 border border-kora-line mt-4 text-left">
                <p className="text-xs font-bold uppercase tracking-wider text-kora-muted mb-2">Centres d'intérêt</p>
                <div className="flex flex-wrap gap-2">
                  {user.interests.map((it) => (
                    <span key={it} className="bg-kora-terracotta/10 text-kora-terracotta px-3 py-1 rounded-full text-xs font-medium">{it}</span>
                  ))}
                </div>
              </div>
            )}

            {user.partner_values?.length > 0 && (
              <div className="bg-white rounded-2xl p-5 border border-kora-line mt-4 text-left">
                <p className="text-xs font-bold uppercase tracking-wider text-kora-muted mb-2">Recherché chez un partenaire</p>
                <div className="flex flex-wrap gap-2">
                  {user.partner_values.map((v) => (
                    <span key={v} className="bg-kora-sage/10 text-kora-sage px-3 py-1 rounded-full text-xs font-medium">{v}</span>
                  ))}
                </div>
              </div>
            )}

            {(() => {
              const items = [
                ["Situation", personalLabel("marital_status", user.marital_status)],
                ["Enfants", personalLabel("has_children", user.has_children)],
                ["Tabac", personalLabel("smoking", user.smoking)],
                ["Alcool", personalLabel("alcohol", user.alcohol)],
              ].filter(([, v]) => v);
              return items.length > 0 ? (
                <div className="bg-white rounded-2xl p-5 border border-kora-line mt-4 text-left">
                  <p className="text-xs font-bold uppercase tracking-wider text-kora-muted mb-2">Informations personnelles</p>
                  <div className="grid grid-cols-2 gap-2">
                    {items.map(([label, v]) => (
                      <div key={label} className="bg-kora-sand rounded-xl px-3 py-2">
                        <p className="text-[10px] uppercase tracking-wider text-kora-muted">{label}</p>
                        <p className="text-sm font-semibold text-kora-ink">{v}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null;
            })()}

            {!user.verified && (
              <button data-testid="verify-profile-btn" onClick={() => setShowVerify(true)} className="w-full mt-4 flex items-center justify-center gap-2 bg-kora-sage text-white rounded-full py-3 font-bold">
                <ShieldCheck size={16} /> Vérifier mon profil (selfie)
              </button>
            )}

            <button data-testid="edit-profile-btn" onClick={() => setEditing(true)} className="w-full mt-4 flex items-center justify-center gap-2 bg-white border border-kora-line rounded-full py-3 font-semibold text-kora-ink">
              <Pencil size={16} /> Modifier le profil
            </button>
          </>
        ) : (
          <div className="bg-white rounded-2xl p-5 border border-kora-line mt-6 text-left space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-kora-muted">Photos</label>
              <div className="mt-2">
                <PhotoUploader photos={user.photos || []} onChange={onPhotos} />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-kora-muted">Prénom</label>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-name" maxLength={40} className="w-full mt-1 px-4 py-2.5 rounded-xl border border-kora-line focus:outline-none focus:border-kora-terracotta" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              {user.is_admin && (
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-kora-muted">Pays d'origine</label>
                  <select value={city} onChange={(e) => setCity(e.target.value)} data-testid="edit-city" className="w-full mt-1 px-4 py-2.5 rounded-xl border border-kora-line bg-white focus:outline-none focus:border-kora-terracotta">
                    <option value="">Choisir…</option>
                    {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-kora-muted">Pays de résidence</label>
                <select value={residenceCountry} onChange={(e) => setResidenceCountry(e.target.value)} data-testid="edit-residence" className="w-full mt-1 px-4 py-2.5 rounded-xl border border-kora-line bg-white focus:outline-none focus:border-kora-terracotta">
                  <option value="">Choisir…</option>
                  {RESIDENCE_COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-kora-muted">Bio</label>
              <textarea value={bio} onChange={(e) => setBio(e.target.value)} data-testid="edit-bio" rows={3} className="w-full mt-1 px-4 py-2.5 rounded-xl border border-kora-line focus:outline-none resize-none" />
            </div>

            <div>
              <p className="text-sm font-semibold text-kora-ink mb-2">💍 Situation matrimoniale</p>
              <CardRadio group="edit-marital-status" options={MARITAL_STATUS} value={maritalStatus} onSelect={setMaritalStatus} />
            </div>

            <div>
              <p className="text-sm font-semibold text-kora-ink mb-2">👨‍👩‍👧 Avez-vous des enfants ?</p>
              <CardRadio group="edit-has-children" options={HAS_CHILDREN} value={hasChildren} onSelect={setHasChildren} />
            </div>

            {/* Informations non modifiables */}
            <div className="pt-1 border-t border-kora-line">
              <p className="text-xs font-bold uppercase tracking-wider text-kora-muted mt-3 mb-2">Informations non modifiables</p>
              <div className="grid grid-cols-2 gap-2" data-testid="readonly-info">
                {[
                  ["Pays d'origine", user.city],
                  ["Pays de résidence", user.residence_country],
                  ["But", user.goal === "mariage" ? "Mariage" : user.goal === "amicale" ? "Amicale" : null],
                  ["Silhouette", user.body_type],
                  ["Taille", user.height ? `${user.height} cm` : null],
                  ["Niveau d'étude", user.education],
                  ["Tabac", personalLabel("smoking", user.smoking)],
                  ["Alcool", personalLabel("alcohol", user.alcohol)],
                ].filter(([, v]) => v).map(([label, v]) => (
                  <div key={label} className="bg-kora-sand rounded-xl px-3 py-2 opacity-80">
                    <p className="text-[10px] uppercase tracking-wider text-kora-muted">{label}</p>
                    <p className="text-sm font-semibold text-kora-ink">{v}</p>
                  </div>
                ))}
              </div>
              {user.interests?.length > 0 && (
                <div className="mt-3">
                  <p className="text-[10px] uppercase tracking-wider text-kora-muted mb-1.5">Centres d'intérêt</p>
                  <div className="flex flex-wrap gap-2">
                    {user.interests.map((it) => <span key={it} className="bg-kora-terracotta/10 text-kora-terracotta px-3 py-1 rounded-full text-xs font-medium">{it}</span>)}
                  </div>
                </div>
              )}
              {user.partner_values?.length > 0 && (
                <div className="mt-3">
                  <p className="text-[10px] uppercase tracking-wider text-kora-muted mb-1.5">Recherché chez un partenaire</p>
                  <div className="flex flex-wrap gap-2">
                    {user.partner_values.map((v) => <span key={v} className="bg-kora-sage/10 text-kora-sage px-3 py-1 rounded-full text-xs font-medium">{v}</span>)}
                  </div>
                </div>
              )}
            </div>

            <button data-testid="save-profile-btn" onClick={save} className="w-full flex items-center justify-center gap-2 bg-kora-terracotta text-white rounded-full py-3 font-bold">
              <Check size={16} /> Enregistrer
            </button>
          </div>
        )}

        {/* Visiteurs */}
        <div className="rounded-2xl p-5 border border-blue-600 mt-4 text-left" style={{ backgroundColor: "#3b82f6" }} data-testid="visitors-section">
          <div className="flex items-center gap-2 mb-3">
            <Users size={16} className="text-white" />
            <p className="text-sm font-bold text-white">{visits.count} visiteur(s) de profil</p>
          </div>
          {!user.premium ? (
            visits.count === 0 ? (
              <p className="text-sm text-white/80">Personne n'a encore consulté votre profil.</p>
            ) : (
              <>
                <div className="flex gap-2 overflow-x-auto kora-scroll" data-testid="visitors-locked">
                  {(visits.teaser_photos || []).slice(0, 8).map((photo, i) => (
                    <button key={i} onClick={() => navigate("/premium")} className="shrink-0 text-center focus:outline-none">
                      <div className="w-14 h-14 rounded-full border-2 border-white/60 overflow-hidden relative">
                        {photo ? (
                          <img src={resolveImg(photo)} alt="Visiteur" className="w-full h-full object-cover blur-[6px] scale-110" draggable={false} />
                        ) : (
                          <div className="w-full h-full bg-white/25" />
                        )}
                      </div>
                      <p className="text-[11px] text-white/90 mt-1 w-14 truncate">•••</p>
                    </button>
                  ))}
                </div>
                <button data-testid="unlock-visitors-btn" onClick={() => navigate("/premium")} className="mt-3 text-xs font-bold text-white flex items-center gap-1">
                  <Lock size={12} /> Voir qui a visité mon profil — Passez Premium
                </button>
              </>
            )
          ) : visits.profiles.length === 0 ? (
            <p className="text-sm text-white/80">Personne n'a encore consulté votre profil.</p>
          ) : (
            <div className="flex gap-2 overflow-x-auto kora-scroll">
              {visits.profiles.slice(0, 8).map((p) => (
                <button key={p.user_id} data-testid={`visitor-${p.user_id}`} onClick={() => setSelectedVisitor(p)} className="shrink-0 text-center focus:outline-none">
                  <img src={resolveImg(p.photo_url)} alt={p.name} className="w-14 h-14 rounded-full object-cover border-2 border-white/60" />
                  <p className="text-[11px] text-white/90 mt-1 w-14 truncate">{p.name}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Notifications push */}
        <div className="bg-white rounded-2xl p-5 border border-kora-line mt-4 text-left" data-testid="push-section">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell size={18} className="text-kora-terracotta" />
              <div>
                <p className="text-sm font-medium text-kora-ink">Notifications push</p>
                <p className="text-xs text-kora-muted">Soyez alerté(e) des likes et messages</p>
              </div>
            </div>
            <Switch data-testid="push-toggle" checked={pushOn} disabled={pushBusy} onCheckedChange={togglePush} />
          </div>
        </div>

        {/* Premium controls */}
        {user.premium && (
          <div className="bg-white rounded-2xl p-5 border border-kora-line mt-4 text-left space-y-4" data-testid="premium-controls">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Star size={18} className="text-blue-500" fill="#3b82f6" />
                <span className="text-sm font-medium text-kora-ink">Super Likes restants</span>
              </div>
              <span data-testid="profile-super-count" className="font-display font-bold text-lg text-blue-500">{user.premium_plan === "master" ? <>∞ <span className="text-sm text-kora-muted">illimités</span></> : <>{user.super_likes_remaining ?? 0}<span className="text-sm text-kora-muted">/5</span></>}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <EyeOff size={18} className="text-kora-sage" />
                <div>
                  <p className="text-sm font-medium text-kora-ink">Mode invisible</p>
                  <p className="text-xs text-kora-muted">Consultez sans apparaître dans les visites</p>
                </div>
              </div>
              <Switch data-testid="invisible-toggle" checked={!!user.invisible_mode} onCheckedChange={toggleInvisible} />
            </div>
          </div>
        )}


        {!user.premium && (
          <button data-testid="go-premium-btn" onClick={() => navigate("/premium")} className="w-full mt-4 flex items-center justify-center gap-2 bg-gradient-to-r from-kora-terracotta to-kora-ochre text-white rounded-full py-3 font-bold animate-kora-pulse">
            <Sparkles size={16} /> Passer Premium
          </button>
        )}

        {user.is_admin && (
          <button data-testid="admin-link-btn" onClick={() => navigate("/admin")} className="w-full mt-4 flex items-center justify-center gap-2 bg-kora-ink text-white rounded-full py-3 font-bold">
            <Shield size={16} /> Espace administrateur
          </button>
        )}

        <button data-testid="logout-btn" onClick={logout} className="w-full mt-4 flex items-center justify-center gap-2 text-kora-muted py-3 font-medium">
          <LogOut size={16} /> Se déconnecter
        </button>

        <button data-testid="account-settings-link" onClick={openAccountMenu} className="w-full mt-1 flex items-center justify-center gap-1.5 text-[12px] text-kora-muted/70 py-2 hover:text-kora-muted transition-colors">
          <Settings size={13} /> Gestion du compte
        </button>
      </div>

      {accountOpen && (
        <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center px-5">
          <div data-testid="account-modal" className="w-full max-w-sm bg-white rounded-3xl p-6 max-h-[90vh] overflow-y-auto">

            {accountStep === "menu" && (
              <>
                <div className="flex items-center gap-2">
                  <Settings size={20} className="text-kora-terracotta" />
                  <h3 className="font-display font-black text-lg text-kora-ink">Gestion du compte</h3>
                </div>
                <div className="mt-4 space-y-2">
                  {user.paused ? (
                    <button data-testid="account-unpause" onClick={unpauseAccount} disabled={pauseBusy} className="w-full flex items-center gap-3 bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 text-left">
                      <PlayCircle size={18} className="text-amber-600 shrink-0" />
                      <span className="text-sm font-bold text-amber-800 flex-1">Réactiver mon compte</span>
                      <ChevronRight size={16} className="text-amber-400" />
                    </button>
                  ) : (
                    <button data-testid="account-pause" onClick={pauseAccount} disabled={pauseBusy} className="w-full flex items-center gap-3 bg-kora-sand rounded-xl px-4 py-3 text-left">
                      <PauseCircle size={18} className="text-kora-terracotta shrink-0" />
                      <span className="flex-1">
                        <span className="block text-sm font-bold text-kora-ink">Mettre mon compte en pause</span>
                        <span className="block text-[11px] text-kora-muted">Invisible pour les autres, réactivable à tout moment</span>
                      </span>
                      <ChevronRight size={16} className="text-kora-muted" />
                    </button>
                  )}
                  <button data-testid="account-premium" onClick={() => { setAccountOpen(false); navigate("/premium"); }} className="w-full flex items-center gap-3 bg-gradient-to-r from-kora-terracotta/10 to-kora-ochre/10 border border-kora-terracotta/30 rounded-xl px-4 py-3 text-left">
                    <Sparkles size={18} className="text-kora-terracotta shrink-0" />
                    <span className="text-sm font-bold text-kora-ink flex-1">KoraLove Premium</span>
                    <ChevronRight size={16} className="text-kora-muted" />
                  </button>
                  <button data-testid="account-privacy" onClick={() => { setAccountOpen(false); navigate("/confidentialite"); }} className="w-full flex items-center gap-3 bg-kora-sand rounded-xl px-4 py-3 text-left">
                    <Lock size={18} className="text-kora-terracotta shrink-0" />
                    <span className="text-sm font-medium text-kora-ink flex-1">Confidentialité</span>
                    <ChevronRight size={16} className="text-kora-muted" />
                  </button>
                  <button data-testid="account-terms" onClick={() => { setAccountOpen(false); navigate("/conditions"); }} className="w-full flex items-center gap-3 bg-kora-sand rounded-xl px-4 py-3 text-left">
                    <FileText size={18} className="text-kora-terracotta shrink-0" />
                    <span className="text-sm font-medium text-kora-ink flex-1">Conditions d'utilisation</span>
                    <ChevronRight size={16} className="text-kora-muted" />
                  </button>
                  <button data-testid="account-logout" onClick={logout} className="w-full flex items-center gap-3 bg-kora-sand rounded-xl px-4 py-3 text-left">
                    <LogOut size={18} className="text-kora-terracotta shrink-0" />
                    <span className="text-sm font-medium text-kora-ink flex-1">Se déconnecter</span>
                    <ChevronRight size={16} className="text-kora-muted" />
                  </button>
                </div>
                <button data-testid="account-close" onClick={() => setAccountOpen(false)} className="w-full mt-4 text-kora-ink py-2.5 rounded-full border border-kora-line text-sm font-semibold">Fermer</button>
                <button data-testid="delete-account-link" onClick={startDeleteFlow} className="w-full mt-3 text-center text-[11px] text-kora-muted/50 underline underline-offset-2 py-1 hover:text-kora-muted transition-colors">
                  Supprimer mon compte
                </button>
              </>
            )}

            {accountStep === "retention" && (
              <div data-testid="retention-screen">
                <h3 className="font-display font-black text-xl text-kora-ink text-center">Attendez ! 💔</h3>
                <p className="text-sm text-kora-muted text-center mt-1.5">Voulez-vous vraiment tout perdre ?</p>
                <div className="grid grid-cols-3 gap-2 mt-4">
                  <div className="bg-kora-sand rounded-xl py-3 text-center">
                    <p className="font-display font-black text-xl text-kora-terracotta" data-testid="stat-matches">{stats ? stats.matches : "…"}</p>
                    <p className="text-[10px] font-bold text-kora-muted uppercase">Matchs</p>
                  </div>
                  <div className="bg-kora-sand rounded-xl py-3 text-center">
                    <p className="font-display font-black text-xl text-kora-terracotta" data-testid="stat-likes">{stats ? stats.likes_received : "…"}</p>
                    <p className="text-[10px] font-bold text-kora-muted uppercase">Likes reçus</p>
                  </div>
                  <div className="bg-kora-sand rounded-xl py-3 text-center">
                    <p className="font-display font-black text-xl text-kora-terracotta" data-testid="stat-visitors">{stats ? stats.visitors : "…"}</p>
                    <p className="text-[10px] font-bold text-kora-muted uppercase">Visites</p>
                  </div>
                </div>
                <p className="text-xs text-kora-muted text-center mt-3">En supprimant votre compte, tout cela disparaîtra définitivement. Besoin d'une simple pause ?</p>
                <button data-testid="retention-pause-btn" onClick={pauseAccount} disabled={pauseBusy} className="w-full mt-4 bg-kora-terracotta text-white rounded-full py-3.5 font-bold hover:bg-[#C94F2B] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                  <PauseCircle size={18} /> Mettre mon compte en pause
                </button>
                <p className="text-[11px] text-kora-muted text-center mt-1.5">Profil invisible, vos matchs et messages sont conservés. Réactivable à tout moment.</p>
                <button data-testid="retention-delete-btn" onClick={() => setAccountStep("reasons")} className="w-full mt-4 text-kora-muted/70 py-2 text-xs underline underline-offset-2">
                  Supprimer définitivement mon compte
                </button>
                <button data-testid="retention-back" onClick={() => setAccountStep("menu")} className="w-full mt-1 text-kora-ink py-2 text-sm font-semibold">Retour</button>
              </div>
            )}

            {accountStep === "reasons" && (
              <div data-testid="delete-account-modal">
                <div className="flex items-center gap-2">
                  <AlertTriangle size={20} className="text-red-600" />
                  <h3 className="font-display font-black text-lg text-kora-ink">Supprimer mon compte</h3>
                </div>
                <p className="text-xs text-kora-muted mt-2">Cette action est définitive : votre profil, vos matchs et vos messages seront supprimés. Dites-nous pourquoi vous partez :</p>
                <label className="flex items-center gap-2.5 mt-4 bg-kora-sand rounded-xl px-3.5 py-3 cursor-pointer">
                  <input data-testid="delete-reason-found" type="checkbox" checked={delReasons.found} onChange={(e) => setDelReasons((r) => ({ ...r, found: e.target.checked }))} className="accent-kora-terracotta w-4 h-4 shrink-0" />
                  <span className="text-sm text-kora-ink font-medium">J'ai déjà trouvé ce que je cherchais</span>
                </label>
                <label className="flex items-center gap-2.5 mt-2 bg-kora-sand rounded-xl px-3.5 py-3 cursor-pointer">
                  <input data-testid="delete-reason-other" type="checkbox" checked={delReasons.other} onChange={(e) => setDelReasons((r) => ({ ...r, other: e.target.checked }))} className="accent-kora-terracotta w-4 h-4 shrink-0" />
                  <span className="text-sm text-kora-ink font-medium">Autres</span>
                </label>
                {delReasons.other && (
                  <textarea
                    data-testid="delete-reason-message"
                    value={delMessage}
                    onChange={(e) => setDelMessage(e.target.value)}
                    placeholder="Dites-nous en quelques mots la raison de votre départ…"
                    rows={3}
                    className="w-full mt-2 px-3.5 py-3 rounded-xl border border-kora-line text-sm focus:outline-none focus:border-kora-terracotta resize-none"
                  />
                )}
                <button data-testid="delete-account-confirm" onClick={confirmDelete} disabled={delBusy} className="w-full mt-4 bg-red-600 text-white rounded-full py-3 font-bold text-sm hover:bg-red-700 transition-colors disabled:opacity-60">
                  {delBusy ? "Suppression…" : "Supprimer définitivement mon compte"}
                </button>
                <button data-testid="delete-account-cancel" onClick={() => setAccountStep("retention")} className="w-full mt-2 text-kora-muted py-2 text-sm font-medium">Retour</button>
              </div>
            )}
          </div>
        </div>
      )}
      <SelfieVerify open={showVerify} onClose={() => setShowVerify(false)} onVerified={() => setUser((u) => ({ ...u, verified: true }))} />
      {selectedVisitor && <ProfileDetailModal profile={selectedVisitor} onClose={() => setSelectedVisitor(null)} onAction={handleVisitorAction} onMessage={handleVisitorMessage} />}
      <BottomNav />
    </div>
  );
};

export default Profile;
